/**
 * InsufficientCreditsDialog Component
 * Shown when a user tries to create a new analysis but has no credits available
 */

import { useTranslation } from 'react-i18next';
import { CreditCard } from 'lucide-react';
import {
  GlassModal,
  GlassModalContent,
  GlassModalHeader,
  GlassModalTitle,
  GlassModalDescription,
} from '@/components/common/GlassModal';
import { Button } from '@/components/ui/button';

interface InsufficientCreditsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onBuyCredits: () => void;
  currentCredits?: number;
}

export function InsufficientCreditsDialog({
  isOpen,
  onClose,
  onBuyCredits,
  currentCredits = 0,
}: InsufficientCreditsDialogProps) {
  const { t } = useTranslation();

  return (
    <GlassModal open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <GlassModalContent className="max-w-3xl" onCloseClick={onClose}>
        <GlassModalHeader>
          <GlassModalTitle>{t('insufficientCredits.title')}</GlassModalTitle>
          <GlassModalDescription>
            {t('insufficientCredits.explanation', { count: currentCredits })}
            {' '}
            {t('insufficientCredits.callToAction')}
          </GlassModalDescription>
        </GlassModalHeader>

        <div className="pt-4 border-t border-[var(--glass-border)] flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-[var(--color-charcoal)]/70 hover:text-[var(--color-charcoal)]"
          >
            {t('insufficientCredits.cancel')}
          </Button>
          <Button
            onClick={onBuyCredits}
            className="btn-primary flex items-center justify-center gap-2"
          >
            <CreditCard className="h-4 w-4" />
            {t('insufficientCredits.buyCredits')}
          </Button>
        </div>
      </GlassModalContent>
    </GlassModal>
  );
}
